import useSWR from 'swr';
import { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Checkbox } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { statesList } from '@shared/constants/statesList';
import { OPERATOR_SYMBOL, OperatorType, operatorsList } from '@shared/constants/operatorsList';
import { TableModal } from '@shared/components/modals/TableModal';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { formatCurrency, formatPercentage } from '@shared/commonFunctions';
import { Wrapper } from '@styles/CommonStyles';
import { LoanRestriction } from './interfaces';
import { requestCreateRestriction, requestDeleteRestriction, requestUpdateRestriction } from './requests';
import { StateProductRestrictions } from './StateProductRestrictions/StateProductRestrictions';

const formConfig = [
  {
    width: '100%',
    name: 'state',
    label: 'State',
    options: statesList,
    component: SelectAutocompleteField,
  },
  {
    width: '50%',
    name: 'loanAmount',
    label: 'Loan Amount',
    prefix: '$',
    decimalScale: 2,
    component: NumberInputField,
  },
  {
    width: '50%',
    name: 'loanAmountOperator',
    label: 'Loan Amount Operator',
    options: operatorsList,
    component: SelectAutocompleteField,
  },
  {
    width: '50%',
    name: 'apr',
    label: 'APR',
    suffix: '%',
    decimalScale: 2,
    component: NumberInputField,
  },
  {
    width: '50%',
    name: 'aprOperator',
    label: 'APR Operator',
    options: operatorsList,
    component: SelectAutocompleteField,
  },
  {
    width: '50%',
    name: 'rate',
    label: 'Rate',
    suffix: '%',
    decimalScale: 2,
    component: NumberInputField,
  },
  {
    width: '50%',
    name: 'rateOperator',
    label: 'Rate Operator',
    options: operatorsList,
    component: SelectAutocompleteField,
  },
  {
    width: '20%',
    name: 'isActive',
    label: 'Active',
    component: CheckboxField,
  },
];

const lienHolderAddressSchema = Yup.object().shape({
  state: Yup.string().required(),
  loanAmount: Yup.string().nullable().max(10, 'Loan Amount must be at most 10 numbers'),
  rate: Yup.string().nullable().max(10, 'Rate must be at most 10 numbers'),
  apr: Yup.string().nullable().max(10, 'APR must be at most 10 numbers'),
  loanAmountOperator: Yup.string().when('loanAmount', {
    is: (value: string) => !!value,
    then: Yup.string().required().nullable(),
    otherwise: Yup.string().nullable(),
  }),
  aprOperator: Yup.string().when('apr', {
    is: (value: string) => !!value,
    then: Yup.string().required().nullable(),
    otherwise: Yup.string().nullable(),
  }),
  rateOperator: Yup.string().when('rate', {
    is: (value: string) => !!value,
    then: Yup.string().required().nullable(),
    otherwise: Yup.string().nullable(),
  }),
});

export const LoanRestrictionsConfiguration = () => {
  const { data: loanRestrictions = [], mutate: loanRestrictionsMutate } = useSWR<LoanRestriction[]>(
    `${SERVICES.LENDER}/loanrestriction`
  );
  const [currentLoanRestriction, setCurrentLoanRestriction] = useState<LoanRestriction | null>(null);
  const [isSubmitting, setSubmittingStatus] = useState(false);

  const columns: GridColDef<LoanRestriction>[] = useMemo(
    () => [
      dataGridCol('state', {
        flex: 1,
        minWidth: 100,
        valueGetter: ({ row }) => row.state || '—',
        renderEditCell: (params) => (
          <GridColSelect
            params={params}
            options={statesList}
          />
        ),
      }),
      dataGridCol('loanAmount', {
        flex: 1,
        minWidth: 100,
        valueGetter: ({ row }) => (row.loanAmount ? formatCurrency(row.loanAmount) : '—'),
      }),
      dataGridCol('loanAmountOperator', {
        flex: 1,
        minWidth: 150,
        valueGetter: ({ row }) => OPERATOR_SYMBOL[row.loanAmountOperator as OperatorType] || null,
      }),
      dataGridCol('apr', {
        flex: 1,
        minWidth: 100,
        headerName: 'APR',
        valueGetter: ({ row }) => (row.apr ? formatPercentage(row.apr) : '—'),
      }),
      dataGridCol('aprOperator', {
        flex: 1,
        minWidth: 150,
        valueGetter: ({ row }) => OPERATOR_SYMBOL[row.aprOperator as OperatorType] || null,
      }),
      dataGridCol('rate', {
        valueGetter: ({ row }) => (row.rate ? formatPercentage(row.rate) : '—'),
        minWidth: 150,
      }),
      dataGridCol('isActive', {
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Checkbox
            disabled
            checked={row.isActive}
          />
        ),
      }),
    ],
    []
  );

  const gridActions = {
    onEdit: (data: LoanRestriction) => {
      setCurrentLoanRestriction(data);
    },
    onDelete: (data: LoanRestriction) => {
      requestDeleteRestriction(data.id);
    },
  };

  const handleSubmit = useCallback(
    async (data: LoanRestriction) => {
      if (typeof data.loanAmount !== 'number' && typeof data.apr !== 'number' && typeof data.rate !== 'number') {
        Notistack.generalError('At least one restriction is required');

        return;
      }

      setSubmittingStatus(true);

      const formattedData = {
        id: data?.id ?? '',
        state: data?.state ?? '',
        loanAmount: data?.loanAmount ?? null,
        loanAmountOperator: data?.loanAmountOperator || null,
        apr: data?.apr ?? null,
        aprOperator: data?.aprOperator || null,
        rate: data?.rate ?? null,
        rateOperator: data?.rateOperator || null,
        isActive: data?.isActive ?? true,
      };

      if (data.id) {
        await requestUpdateRestriction(formattedData);
      } else {
        await requestCreateRestriction(formattedData);
      }

      await loanRestrictionsMutate();

      setCurrentLoanRestriction(null);
      setSubmittingStatus(false);
    }, 
    []
  );

  const handleCreate = () => {
    setCurrentLoanRestriction({} as LoanRestriction);
  };

  const editingData = useMemo(
    () => ({
      id: currentLoanRestriction?.id ?? '',
      state: currentLoanRestriction?.state ?? '',
      loanAmount: currentLoanRestriction?.loanAmount ?? null,
      loanAmountOperator: currentLoanRestriction?.loanAmountOperator || null,
      apr: currentLoanRestriction?.apr ?? null,
      aprOperator: currentLoanRestriction?.aprOperator || null,
      rate: currentLoanRestriction?.rate ?? null,
      rateOperator: currentLoanRestriction?.rateOperator || null,
      isActive: currentLoanRestriction?.isActive ?? true,
    }),
    [currentLoanRestriction]
  );

  return (
    <>
      <Wrapper>
      <TableModal
        isOpen={!!currentLoanRestriction}
        editingData={editingData}
        title="Loan Restriction Configuration Page"
        formConfig={formConfig}
        validationSchema={lienHolderAddressSchema}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onClose={() => setCurrentLoanRestriction(null)}
      />

      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        strict
        loading={!loanRestrictions}
        isRowSelectable={() => false}
        buttonTitle="Add New"
        name="Loan Restrictions Configuration"
        columns={columns}
        actions={gridActions}
        rows={loanRestrictions}
        pageSize={5}
        onButtonClick={handleCreate}
      />
    </Wrapper>
      <StateProductRestrictions />
    </>
  );
};
